import { message } from "antd";
import { type FunctionComponent, type ReactNode, useCallback, useState } from "react";
import Lightbox, {
    type IBaseSlide,
    type IDownloadFunctionProps,
    type IVimeoSlide,
    type RenderSlideProps,
    type ViewCallbackProps,
} from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Counter from "yet-another-react-lightbox/plugins/counter";
import Download from "yet-another-react-lightbox/plugins/download";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Video from "yet-another-react-lightbox/plugins/video";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import { cn } from "@btnet/utilities-core";
import { useWebviewContext } from "@btnet/utilities-webview";

import {
    BdsButton,
    BdsIconCaretLargeLeft,
    BdsIconCaretLargeRight,
    BdsIconDownload,
    BdsIconMagnifyingGlassMinus,
    BdsIconMagnifyingGlassPlus,
    BdsIconPause,
    BdsIconPlay,
    BdsIconX,
} from "@buildertrend/components";

import { showAPIErrorMessage } from "utilities/apiHandler";
import {
    generateExternalAttachmentFromLink,
    triggerBrowserDownload,
} from "utilities/document/fileDownload.utils";
import { getBlobFromUrl } from "utilities/file/file";
import { lazyLoadWithRetry } from "utilities/react.utilities";
import { type AllOrNone } from "utilities/type/AllOrNone";

import {
    is360PhotoSlide,
    isVimeoVideoSlide,
    shouldAutoplayVideo,
} from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";
import { FocusProvider } from "commonComponents/utilities/Focus/FocusProvider";
import { JobIdTypes } from "commonComponents/utilities/JobPicker/JobPicker.types";
import { VideoPlayer } from "commonComponents/utilities/VideoPlayer/VideoPlayer";

import {
    type IVideoListHandler,
    VideoListHandler,
} from "entity/video/VideoList/VideoList.api.handler";

import "yet-another-react-lightbox/plugins/captions.css";
import "yet-another-react-lightbox/plugins/counter.css";
import "yet-another-react-lightbox/styles.css";

import "./BTLightbox.less";

const Pannellum = lazyLoadWithRetry(() => import("../../utilities/Pannellum/PannellumWrapper"));

interface ICommonBTLightboxProps {
    /**
     * Whether or not the Lightbox is open
     */
    open: boolean;
    /**
     * Callback when the Lightbox closes. You probably want to set the parent's open state to false here
     */
    onClose(): void;
    /**
     * The slides that the Lightbox will display and navigate through
     */
    slides: IBaseSlide[];
    /**
     * Optional video handler - specifically so we can mock it in tests
     */
    videoHandler?: IVideoListHandler;
    className?: string;
}

interface IControlledBTLightboxProps {
    /**
     * Controls the index being displayed on the Lightbox.
     * By default, the Lightbox will start at index 0 and maintain it's own state.
     * You must provide the onSlideChange callback and maintain the state yourself when using this prop.
     */
    index: number;
    /**
     * Callback when a new slide becomes visible in the Lightbox
     * @param index the index of the slide
     * @param slide the slide object
     */
    onSlideChange(index: number, slide: IBaseSlide): void;
}

export type IBTLightboxProps = ICommonBTLightboxProps & AllOrNone<IControlledBTLightboxProps>;

const defaultVideoHandler = new VideoListHandler();

const downloadSuccessMessage = "Download complete.";

export const BTLightbox: FunctionComponent<IBTLightboxProps> = ({
    open,
    onClose,
    slides,
    index,
    onSlideChange,
    videoHandler = defaultVideoHandler,
    className,
}) => {
    const [isSlideshowPlaying, setIsSlideshowPlaying] = useState(false);
    const [zoom, setZoom] = useState(1);
    const isWebview = useWebviewContext();

    const isZoomed = zoom > 1;
    const maxZoomPixelRatio = 10;

    const handleClose = useCallback(() => {
        setIsSlideshowPlaying(false);
        setZoom(1);
        onClose();
    }, [onClose]);

    const handleSlideView = useCallback(
        ({ index }: ViewCallbackProps) => {
            onSlideChange?.(index, slides[index]);
        },
        [onSlideChange, slides],
    );

    const handleSlideshowStart = () => {
        setIsSlideshowPlaying(true);
    };

    const handleSlideshowStop = () => {
        setIsSlideshowPlaying(false);
    };

    const handleZoomChange = (zoomData: { zoom: number }) => {
        setZoom(zoomData.zoom);
    };

    const downloadSlideMedia = async (url: string, filename: string) => {
        try {
            const blob = await getBlobFromUrl(url);
            triggerBrowserDownload(blob, filename);
            void message.success(downloadSuccessMessage);
        } catch {
            void message.error(
                "An error has occurred trying to download this file, please try again later.",
            );
        }
    };

    const handleCustomSlideDownload = ({ slide, saveAs }: IDownloadFunctionProps) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (slide.type === "custom-video") {
            const vimeoSlide = slide as IVimeoSlide;
            void loadData();
            async function loadData() {
                try {
                    if (!vimeoSlide.vimeoId) {
                        void message.error("Unable to download this video.");
                        return;
                    }
                    const videoID = Number(vimeoSlide.vimeoId);
                    const response = await videoHandler.getVideoDownloadLinks(
                        [videoID],
                        vimeoSlide.metadata?.jobId ?? JobIdTypes.AllJobs,
                    );

                    if (response.linksList?.length > 0) {
                        await Promise.all(
                            response.linksList.map(
                                async (linkUrl, i) =>
                                    await generateExternalAttachmentFromLink(linkUrl, i),
                            ),
                        );
                    } else if (response.linksList?.length === 0) {
                        void message.error("This video is unable to be downloaded.");
                    }
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            }
        }
        // If slides were provided via the mediaDataToSlide or btFileSystemToSlide utility function, they will have a download object
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        else if (slide.download) {
            const downloadSlide = slide as IBaseSlide;
            if (downloadSlide.download && typeof downloadSlide.download === "object") {
                if (isWebview) {
                    void downloadSlideMedia(
                        downloadSlide.download.url,
                        downloadSlide.download.filename,
                    );
                } else {
                    saveAs(downloadSlide.download.url, downloadSlide.download.filename);
                    void message.success(downloadSuccessMessage);
                }
            }
        }
        // If slides were built manually, attempt to download the piece of media from src property.
        // In a webview saveAs silently no-ops, so route through the blob-fetch path (which only
        // reports success once the download actually happens) instead of a false success toast.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        else if (slide.src) {
            const srcSlide = slide as IBaseSlide;
            if (isWebview) {
                void downloadSlideMedia(srcSlide.src, srcSlide.title ?? "download");
            } else {
                saveAs(srcSlide.src);
                void message.success(downloadSuccessMessage);
            }
        } else {
            void message.error(
                "An error has occurred trying to download this file, please try again later.",
            );
        }
    };

    const renderSlide = ({ slide, offset }: RenderSlideProps) => {
        if (isVimeoVideoSlide(slide)) {
            return (
                <FocusProvider disabled={!open} hotkeysToRemove={["navRight", "navLeft"]}>
                    <VideoPlayer
                        className="VideoMediaView"
                        vimeoUrl={slide.src}
                        autoPlay={shouldAutoplayVideo(offset, isSlideshowPlaying)}
                        vimeoId={slide.vimeoId}
                    />
                </FocusProvider>
            );
        }

        if (is360PhotoSlide(slide)) {
            return (
                // We have to stop the pointer down from propagating up so the slide doesn't change
                <div onPointerDown={(e) => e.stopPropagation()}>
                    {/* Empty height/width overridden by less styles */}
                    <FocusProvider disabled={!open} hotkeysToRemove={["navRight", "navLeft"]}>
                        <Pannellum
                            width=""
                            height=""
                            autoLoad
                            image={slide.src}
                            showFullscreenCtrl={true}
                        />
                    </FocusProvider>
                </div>
            );
        }

        // if the slide type wasn't one of these custom types, it is a yarl native type
        // return undefined here so that yarl will handle the rendering of the slide
        return undefined;
    };

    const renderNavigation = slides.length <= 1 ? () => null : undefined;

    const renderHeaderButton = (icon: ReactNode, ariaLabel: string, disabled: boolean = false) => {
        return (
            <BdsButton
                icon={icon}
                ariaLabel={ariaLabel}
                className="Icon"
                testid="lightboxCloseButton"
                displayType="tertiary"
                disabled={disabled}
            />
        );
    };

    return (
        <div
            onKeyDown={(e) => {
                // prevent base modal from closing
                if (e.key === "Escape") {
                    e.stopPropagation();
                }
            }}
        >
            <BTSuspense>
                <FocusProvider disabled={!open} hotkeysToRemove={["navRight", "navLeft"]}>
                    <Lightbox
                        className={cn(
                            "BTLightbox",
                            {
                                Slideshow: isSlideshowPlaying,
                                Pannable: isZoomed,
                            },
                            className,
                        )}
                        open={open}
                        close={handleClose}
                        slides={slides}
                        index={index}
                        plugins={[Zoom, Counter, Captions, Slideshow, Video, Download]}
                        zoom={{ maxZoomPixelRatio: maxZoomPixelRatio }}
                        counter={{
                            separator: "/",
                            className: "Counter",
                        }}
                        slideshow={{ autoplay: false }}
                        on={{
                            view: handleSlideView,
                            slideshowStart: handleSlideshowStart,
                            slideshowStop: handleSlideshowStop,
                            zoom: handleZoomChange,
                        }}
                        carousel={{
                            finite: slides.length <= 1,
                            preload: 1,
                            imageProps: {
                                draggable: !isZoomed,
                            },
                        }}
                        render={{
                            slide: renderSlide,
                            buttonPrev: renderNavigation,
                            buttonNext: renderNavigation,
                            buttonSlideshow: renderNavigation,
                            iconDownload: () => renderHeaderButton(<BdsIconDownload />, "Download"),
                            iconSlideshowPlay: () => renderHeaderButton(<BdsIconPlay />, "Play"),
                            iconSlideshowPause: () => renderHeaderButton(<BdsIconPause />, "Pause"),
                            iconZoomIn: () =>
                                renderHeaderButton(
                                    <BdsIconMagnifyingGlassPlus />,
                                    "Zoom In",
                                    zoom >= maxZoomPixelRatio,
                                ),
                            iconZoomOut: () =>
                                renderHeaderButton(
                                    <BdsIconMagnifyingGlassMinus />,
                                    "Zoom Out",
                                    !isZoomed,
                                ),
                            iconClose: () => renderHeaderButton(<BdsIconX />, "Close"),
                            iconNext: () => renderHeaderButton(<BdsIconCaretLargeRight />, "Next"),
                            iconPrev: () =>
                                renderHeaderButton(<BdsIconCaretLargeLeft />, "Previous"),
                        }}
                        download={{ download: handleCustomSlideDownload }}
                    />
                </FocusProvider>
            </BTSuspense>
        </div>
    );
};
